<template>
  <div
    class="relative flex flex-col"
    :class="colSpan"
  >
    <h3
      v-if="showLabel && field?.display"
      class="typo-h-5 w-full text-current"
    >
      {{ field.display }}
    </h3>
    <p v-if="showLabel && (instructions || field.instructions)" class="mt-1">
      {{ instructions || field.instructions }}
    </p>
    <ul
      class="flex flex-1 flex-col flex-wrap"
      :class="[
        {'mt-2': showLabel && (field?.display || instructions || field.instructions)},
        optionsHighlighted ? 'space-y-4' : 'space-y-2',
      ]"
    >
      <li
        v-for="(option, index) in computedOptions"
        :key="index"
        :class="optionsHighlighted ? 'w-full' : 'w-fit'"
      >
        <!-- eslint-disable readable-tailwind/sort-classes //it automatically sorts the classes in the wrong order and then complains -->
        <label
          class="checkbox-container relative block cursor-pointer select-none"
          :class="[
            optionsHighlighted ?
              `
                rounded-bl-2xl py-4 pl-10 pr-4 transition-all duration-500
                ease-out
                has-[:checked]:duration-200 has-[:checked]:ease-in
              ` :
              'pl-7',

            {
              'bg-gray-500/10 hover:bg-gray-500/15 has-[:checked]:bg-red-500': optionsHighlighted && containerBackground === 'white',
              'bg-white/10 hover:bg-white/25 has-[:checked]:bg-white': optionsHighlighted && containerBackground === 'colored',
            },
          ]"
          :for="option.value"
        >
          <!-- eslint-enable readable-tailwind/sort-classes -->

          <Field
            :id="option.value"
            :value="option.value"
            :name="name || handle || field.handle"
            :label="label || field.label || field.display"
            :disabled="disabled"
            :rules="rulesAsString"
            type="radio"
            class="
              peer absolute right-0 z-0 cursor-pointer hyphens-auto opacity-0
            "
          />
          <span :class="{ 'typo-h-5 text-current peer-checked:text-black': optionsHighlighted }">
            {{ option.label }}
          </span>

          <span
            class="
              checkbox-checkmark absolute block size-[1em] translate-y-[0.05em]
              rounded-full border-[0.125em] border-current duration-100
              ease-in-out
              peer-focus-visible:ring peer-focus-visible:ring-offset-2
            "
            :class="[
              optionsHighlighted ? 'left-3 top-5' : 'left-0 top-1.5',
              optionsHighlighted && containerBackground === 'white' ?
                `after:bg-white` :
                `after:bg-red-500`,
              {
                'peer-checked:border-white': optionsHighlighted && containerBackground === 'white',
                'peer-checked:border-black': optionsHighlighted && containerBackground === 'colored',
              },
            ]"
          />
        </label>
      </li>
    </ul>

    <!-- ERROR -->
    <TransitionExpand
      class="transition-all duration-300 ease-in-out"
      :open="!!error"
    >
      <p
        v-if="error"
        class="hyphens-auto pt-1 text-sm"
        :class="[
          {
            'text-red-500': containerBackground === 'white',
            'text-white': containerBackground === 'colored',
          },
        ]"
      >
        {{ error }}
      </p>
    </TransitionExpand>
  </div>
</template>

<!-- eslint-disable-next-line vue/block-lang -->
<script setup>
import { Field } from 'vee-validate'

defineEmits(['update:modelValue'])

const props = defineProps({
  options: {
    type: [Array, Object],
    default: () => [],
  },

  handle: {
    type: String,
    default: null,
  },

  field: {
    type: Object,
    default: null,
  },

  width: {
    type: [Number, String],
    default: null,
  },

  rules: {
    type: [Array, String],
    default: '',
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  label: {
    type: String,
    default: '',
  },

  name: {
    type: String,
    default: null,
  },

  instructions: {
    type: String,
    default: null,
  },

  error: {
    type: String,
    default: null,
  },

  showLabel: {
    type: Boolean,
    default: true,
  },

  containerBackground: {
    type: String,
    default: 'white',
  },

  optionsHighlighted: {
    type: Boolean,
    default: false,
  },
})

const rulesAsString = useFilterVeeValidateRules(props.rules)

const colSpan = computed(() => {
  const width = parseInt(props.width) || props.field?.width || 100

  switch (width) {
    case 25:
      return 'lg:col-span-3 col-span-12'
    case 33:
      return 'lg:col-span-4 col-span-12'
    case 50:
      return 'lg:col-span-6 col-span-12'
    case 66:
      return 'lg:col-span-8 col-span-12'
    default:
      return 'lg:col-span-12 col-span-12'
  }
})

const computedOptions = computed(() => {
  if (Array.isArray(props.options)) {
    return props.options.map((option) => {
      if (typeof option === 'string') {
        return {
          label: option,
          value: option,
        }
      }

      return {
        label: option.value,
        value: option.key,
      }
    })
  }

  return Object.keys(props.options).map(key => ({
    label: props.options[key],
    value: key,
  }))
})
</script>

<style scoped>
.checkbox-checkmark:after {
  @apply absolute size-2 transition-opacity opacity-0 rounded-full duration-150 ease-in-out ;
  content: '';
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.checkbox-container input:checked ~ .checkbox-checkmark:after {
  @apply opacity-100;
}
</style>
